import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
// @ts-ignore
import { SaveStates } from "@/src/enums";

export function useSavableState() {
  const router = useRouter();

  const majorError = ref<any>(null);
  const formError = ref<any>(null);
  const currentState = ref<any>(SaveStates.Blank);

  const getSavableState = computed(() => currentState.value);
  const isSavableStateLoading = computed(() => currentState.value === SaveStates.Loading);
  const isSavableStateIdle = computed(() => currentState.value === SaveStates.Idle);
  const isSavableStateProcessing = computed(() => currentState.value === SaveStates.Processing);
  const isSavableStateBlank = computed(() => currentState.value === SaveStates.Blank);
  const isSavableStateSuccess = computed(() => currentState.value === SaveStates.Success);
  const isSavableStateErrored = computed(() => currentState.value === SaveStates.Errored);
  const isSavableStateInfo = computed(() => currentState.value === SaveStates.Info);

  function setSavableState(state: any) { currentState.value = state; }
  function setSavableStateLoading() { setSavableState(SaveStates.Loading); }
  function setSavableStateProcessing() { setSavableState(SaveStates.Processing); }
  function setSavableStateIdle() { setSavableState(SaveStates.Idle); }
  function setSavableStateBlank() { setSavableState(SaveStates.Blank); }
  function setSavableStateSuccess() { setSavableState(SaveStates.Success); }
  function setSavableStateErrored() { setSavableState(SaveStates.Errored); }
  function setSavableStateInfo() { setSavableState(SaveStates.Info); }

  function handleMajorError(error: any) { setSavableStateErrored(); majorError.value = error; }
  function handleFormError(error: any) { setSavableStateErrored(); formError.value = error; }

  function switchToSuccessOnCreate(routeToMatch: string) {
    setSavableStateIdle();
    const history = router.options.history.state;
    if (!history.replaced && history.back === routeToMatch) {
      setTimeout(() => { setSavableStateSuccess(); }, 100);
    }
  }

  async function performAsyncLoading(callbackfn: () => Promise<void>) {
    try {
      setSavableStateLoading();
      await callbackfn();
    } catch (error) {
      handleMajorError(error);
    } finally {
      setSavableStateIdle();
    }
  }

  async function loadAsync<T>(callbackfn: () => Promise<T | undefined>): Promise<T | undefined> {
    try {
      setSavableStateLoading();
      const data = await callbackfn();
      setSavableStateIdle();
      return data;
    } catch (error) {
      handleMajorError(error);
    }
  }

  async function processAsync<T>(callbackfn: () => Promise<T | undefined>): Promise<T | undefined> {
    try {
      setSavableStateProcessing();
      const data = await callbackfn();
      setSavableStateIdle();
      return data;
    } catch (error) {
      handleMajorError(error);
    }
  }

  async function performAsyncProcessing(callbackfn: () => Promise<void>) {
    try {
      setSavableStateProcessing();
      await callbackfn();
    } catch (error) {
      handleFormError(error);
    } finally {
      setSavableStateIdle();
    }
  }

  return {
    majorError,
    formError,
    currentState,
    getSavableState,
    isSavableStateLoading,
    isSavableStateIdle,
    isSavableStateProcessing,
    isSavableStateBlank,
    isSavableStateSuccess,
    isSavableStateErrored,
    isSavableStateInfo,
    setSavableState,
    setSavableStateLoading,
    setSavableStateProcessing,
    setSavableStateIdle,
    setSavableStateBlank,
    setSavableStateSuccess,
    setSavableStateErrored,
    setSavableStateInfo,
    handleMajorError,
    handleFormError,
    switchToSuccessOnCreate,
    performAsyncLoading,
    performAsyncProcessing,
    loadAsync,
    processAsync
  };
}
